import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import useSWR from "swr";

import { ArtRow, KbSearchBox } from "../components/KbCards";
import { Icon } from "../components/Icon";
import { api, type KbAssistResponse, type KbPublicPage } from "../services/api";

/**
 * KB search results: a synthesised AI answer box with cited sources (degrading
 * to an extractive answer when the assist feature is off), followed by the
 * full keyword result list.
 */
export function KbSearch() {
  const [params] = useSearchParams();
  const query = params.get("q") ?? "";

  const { data: assist } = useSWR<KbAssistResponse>(
    query ? `/api/kb/assist?q=${encodeURIComponent(query)}` : null,
    () => api.kb.assist(query),
  );
  const { data: results } = useSWR<KbPublicPage>(
    query ? `/api/kb?q=${encodeURIComponent(query)}` : null,
    () => api.kb.list({ q: query, page_size: 25 }),
  );

  const items = useMemo(() => results?.items ?? [], [results]);
  const showAnswer = assist && (assist.kind === "ai" || assist.kind === "extractive") && assist.answer;

  return (
    <div className="kb-page">
      <div className="crumb">
        <Link className="back" to="/kb">
          <Icon name="arrowLeft" size={15} />
          Back to Knowledge Base
        </Link>
      </div>
      <h1 className="page-title" style={{ fontSize: 24, marginBottom: 18 }}>
        Results for &ldquo;{query}&rdquo;
      </h1>
      <KbSearchBox initial={query} />

      <div style={{ marginTop: 28 }}>
        {showAnswer && (
          <div className="kb-assist">
            <div className="kb-assist-head">
              <span className="kb-assist-spark">
                <Icon name="sparkles" size={17} />
              </span>
              <div>
                <div className="kb-assist-title">Answer</div>
                <div className="kb-assist-by">iQuest Assist · from the knowledge base</div>
              </div>
            </div>
            <div className="kb-assist-body">{assist!.answer}</div>
            {assist!.sources.length > 0 && (
              <div className="kb-assist-sources">
                <div className="kb-assist-srclabel">Sources</div>
                <div className="kb-src-chips">
                  {assist!.sources.map((s) => (
                    <Link key={s.id} to={`/kb/article/${s.id}`} className="kb-src-chip">
                      <Icon name="doc" size={14} />
                      {s.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <div className="kb-assist-disclaimer">
              <Icon name="info" size={13} />
              AI-assisted summary. Always confirm steps against the linked article.
            </div>
          </div>
        )}

        <div className="kb-results-count">
          {items.length} {items.length === 1 ? "article" : "articles"}
        </div>
        {items.length > 0 ? (
          <div className="kb-art-list">
            {items.map((a) => (
              <ArtRow key={a.id} article={a} />
            ))}
          </div>
        ) : (
          <div className="kb-empty">
            <div className="kb-empty-ico">
              <Icon name="search" size={26} />
            </div>
            <h3>No articles matched</h3>
            <p>
              Try different words or browse the categories. Still stuck? Raise a General IT Support
              request in Service Hub and the desk will help.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default KbSearch;
